/**
 * buildAllDayEventsProvider — colocated all-day event provider for
 * the canonical event renderer. Parallel to `buildEventSpans` (which
 * paints partial-day spans), this surface paints all-day cells that
 * span horizontally across the timeline's row.
 *
 * Lifted out of `CalendarListAliasPlugin.makeTimelineProvider` so the
 * aggregator no longer owns event-specific all-day rendering
 * knowledge — the event renderer module does. The aggregator
 * delegates here per row.
 *
 * Per permissive-renderer-architecture migration plan §Phase D.
 */

import type { NaiveDate } from "@tento-chrono";
import type { EventItemAllDay } from "@tento-lona/sheets";
import type { EventItemPresentation } from "@tento-lona";
import type { EventAccess } from "@tento-lona/sheets-ui/sheet/gestures/event-access.ts";
import type { TimelineContentProvider } from "@tento-lona/sheets-ui/plugins/layout/timeline-shared/timeline-content-provider.ts";
import { AllDayEventsCell } from "@tento-lona/sheets-ui/plugins/renderer/timeline-row-event/all-day-events/cell.ts";
import { MoreIndicator } from "@tento-lona/sheets-ui/plugins/renderer/timeline-row-event/all-day-events/more-indicator.ts";
import { EventTooltip } from "@tento-lona/sheets-ui/plugins/renderer/column-calendar/components/event-tooltip.ts";

export interface BuildAllDayProviderInput {
  /** Filtered + range-resolving fetcher for full-day events.
   *  Caller applies category/visibility filters; this builder
   *  consumes the result. */
  items(range: NaiveDate.Range): EventItemAllDay[];
  /** Loader for an arbitrary range. Called by the host when the
   *  visible window changes. */
  ensureColumnsLoaded(columns: NaiveDate.Partial.WithTz.Range): Promise<void>;
  /** The client's event categories and drafts (`client.events`): titles and colours. */
  readonly presentation: EventItemPresentation.Scope;
  /** The access of the calendar list the items come from (`Provider.calendarListAccess`). */
  calendarListAccess(): EventAccess.List;
}

function buildAllDayEventsProvider(
  input: BuildAllDayProviderInput,
): TimelineContentProvider.Provider<EventItemAllDay> {
  const { items, ensureColumnsLoaded, presentation, calendarListAccess } = input;
  return {
    items,
    calendarListAccess,
    ensureColumnsLoaded: ensureColumnsLoaded,
    makeCell: () => AllDayEventsCell.make(),
    bindCell: ($cell, item, today) => {
      ($cell as AllDayEventsCell).bind(item, today, presentation);
    },
    gestureItem: (_item) => null,
    dateRange: (item) => item.time,
    itemId: (item) => item.id,
    makeOverflowIndicator: () => MoreIndicator.make(),
    bindOverflowIndicator: (
      $indicator,
      itemsList,
      handle,
      position,
      visibleRows,
    ) => {
      ($indicator as MoreIndicator).bind(
        itemsList,
        presentation,
        () =>
          handle.tooltip.showTooltip(
            "all-day-event",
            EventTooltip.instance.bind(itemsList, presentation),
            position,
            "bottom",
            null,
          ),
        () => handle.tooltip.hideTooltip("all-day-event"),
        visibleRows,
      );
    },
  };
}

export namespace AllDayEventsProvider {
  export const build = buildAllDayEventsProvider;
}
